/** Select the latest message independently of socket delivery order. */
const getLatestMessage = (first, second) => {
  if (!first || !second) return first || second;
  if (first.created_at !== second.created_at) {
    return first.created_at > second.created_at ? first : second;
  }
  if (typeof first.id !== 'number') {
    return typeof second.id !== 'number' ? second : first;
  }
  if (typeof second.id !== 'number') return second;

  return first.id >= second.id ? first : second;
};

/**
 * Filters out duplicate source messages from an array of messages.
 * @param {Array} messages - The array of messages to filter.
 * @returns {Array} An array of messages without duplicates.
 */
export const filterDuplicateSourceMessages = (messages = []) => {
  const messagesWithoutDuplicates = [];
  // We cannot use Map or any short hand method as it returns the last message with the duplicate ID
  // We should return the message with smaller id when there is a duplicate
  messages.forEach(m1 => {
    if (m1.source_id) {
      const index = messagesWithoutDuplicates.findIndex(
        m2 => m1.source_id === m2.source_id
      );

      if (index < 0) {
        messagesWithoutDuplicates.push(m1);
      }
    } else {
      messagesWithoutDuplicates.push(m1);
    }
  });
  return messagesWithoutDuplicates;
};

/**
 * Retrieves the last message from a conversation, prioritizing non-activity messages.
 * @param {Object} m - The conversation object containing messages.
 * @returns {Object} The last message of the conversation.
 */
export const getLastMessage = m => {
  let latestMessage;
  let latestNonActivityMessage;
  m.messages.forEach(message => {
    latestMessage = getLatestMessage(latestMessage, message);
    if (message.message_type !== 2) {
      latestNonActivityMessage = getLatestMessage(
        latestNonActivityMessage,
        message
      );
    }
  });

  return (
    getLatestMessage(latestNonActivityMessage, m.last_non_activity_message) ||
    latestMessage
  );
};

/**
 * Filters messages that have been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of read messages.
 */
export const getReadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 <= agentLastSeenAt * 1000
  );
};

/**
 * Filters messages that have not been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of unread messages.
 */
export const getUnreadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 > agentLastSeenAt * 1000
  );
};
